"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/** 드래그가 이 비율을 넘어야 다음 스냅으로 넘어간다 (검색 드로어와 동일). */
const DRAG_THRESHOLD = 0.05;

export interface SnapSheet {
  /** 현재 높이 비율(0~1). 컨테이너 높이에 곱해 쓴다. */
  snap: number;
  setSnap: (value: number) => void;
  isDragging: boolean;
  /** 드래그 핸들에 펼쳐 붙인다. */
  handleProps: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    onMouseDown: (e: React.MouseEvent) => void;
  };
  /** 시트 본문(스크롤 컨테이너)의 ref 콜백 — 경계에서 시트 높이를 조절한다. */
  attachScroll: (node: HTMLDivElement | null) => void;
  contentProps: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
}

/**
 * 손수 만든 스냅 드로어 — `SearchPageClient` 의 상품 드로어와 같은 방식
 * (스냅 포인트 사이 드래그 · 스크롤 경계에서 높이 조절 · native touchmove 로 preventDefault).
 * 허브는 스냅이 36% / 100% 라 포인트만 인자로 받는다. 기존 드로어는 건드리지 않는다.
 *
 * @param snapPoints 오름차순 비율 배열. 모듈 상수로 고정해 넘길 것(렌더마다 새 배열 금지).
 * @param containerRef 시트가 차지하는 전체 높이의 기준 컨테이너(지도+시트 래퍼).
 * @param options.initialSnap 첫 렌더 높이 — URL 복원 시 0.15 에서 애니메이션되지 않도록
 *   처음부터 그 값으로 시작한다.
 * @param options.onSettle 드래그가 스냅에 붙은 순간(애니메이션이 정해진 뒤) 한 번 호출.
 */
export function useSnapSheet(
  snapPoints: readonly number[],
  containerRef: React.RefObject<HTMLElement | null>,
  options?: { initialSnap?: number; onSettle?: (snap: number) => void },
): SnapSheet {
  const minSnap = snapPoints[0];
  const [snap, setSnap] = useState<number>(options?.initialSnap ?? minSnap);
  const [isDragging, setIsDragging] = useState(false);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const dragStartY = useRef<number | null>(null);
  const dragStartSnap = useRef<number>(minSnap);
  const contentDragStartY = useRef<number | null>(null);
  const initialScrollState = useRef<{ isAtTop: boolean; isAtBottom: boolean }>({
    isAtTop: true,
    isAtBottom: false,
  });
  const isContentDragging = useRef<boolean>(false);

  // native 리스너에서 최신 snap 을 읽기 위한 미러(ref 대입은 setState 가 아니다).
  const snapRef = useRef(snap);
  useEffect(() => {
    snapRef.current = snap;
  }, [snap]);

  // onSettle 은 렌더마다 새 함수일 수 있으므로 미러로 잡아 콜백 identity 를 고정한다.
  const onSettleRef = useRef(options?.onSettle);
  useEffect(() => {
    onSettleRef.current = options?.onSettle;
  }, [options?.onSettle]);

  const clamp = useCallback(
    (value: number) => Math.min(1, Math.max(minSnap, value)),
    [minSnap],
  );

  const handleDragStart = useCallback((clientY: number) => {
    dragStartY.current = clientY;
    dragStartSnap.current = snapRef.current;
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback(
    (clientY: number) => {
      if (dragStartY.current === null || !containerRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      if (!containerHeight) return;
      const deltaRatio = (dragStartY.current - clientY) / containerHeight;
      setSnap(clamp(dragStartSnap.current + deltaRatio));
    },
    [clamp, containerRef],
  );

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null) return;

    const dragDelta = dragStartSnap.current - snapRef.current;
    let targetSnap = dragStartSnap.current;

    if (Math.abs(dragDelta) > DRAG_THRESHOLD) {
      const currentIndex = snapPoints.indexOf(dragStartSnap.current);
      if (dragDelta > 0 && currentIndex > 0) {
        targetSnap = snapPoints[currentIndex - 1];
      } else if (dragDelta < 0 && currentIndex < snapPoints.length - 1) {
        targetSnap = snapPoints[currentIndex + 1];
      } else if (currentIndex === -1) {
        // 드래그 도중 값(스냅 사이)에서 손을 떼면 가까운 스냅으로 붙인다.
        targetSnap = snapPoints.reduce((best, point) =>
          Math.abs(point - snapRef.current) < Math.abs(best - snapRef.current)
            ? point
            : best,
        );
      }
    }

    setSnap(targetSnap);
    dragStartY.current = null;
    setIsDragging(false);
    onSettleRef.current?.(targetSnap);
  }, [snapPoints]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientY);

      const onMove = (ev: MouseEvent) => handleDragMove(ev.clientY);
      const onUp = () => {
        handleDragEnd();
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [handleDragStart, handleDragMove, handleDragEnd],
  );

  const attachScroll = useCallback((node: HTMLDivElement | null) => {
    setScrollEl(node);
  }, []);

  const handleContentTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const el = scrollEl;
      const isAtTop = !el || el.scrollTop <= 0;
      const isAtBottom = el
        ? el.scrollTop + el.clientHeight >= el.scrollHeight - 1
        : false;
      contentDragStartY.current = e.touches[0].clientY;
      initialScrollState.current = { isAtTop, isAtBottom };
      isContentDragging.current = false;
    },
    [scrollEl],
  );

  const handleContentTouchEnd = useCallback(() => {
    if (isContentDragging.current) handleDragEnd();
    contentDragStartY.current = null;
    isContentDragging.current = false;
  }, [handleDragEnd]);

  // React 의 onTouchMove 는 passive 라 preventDefault 가 막힌다 —
  // 스크롤 경계에서 시트를 실제로 끌어올리려면 native 리스너여야 한다.
  useEffect(() => {
    const el = scrollEl;
    const container = containerRef.current;
    if (!el || !container) return;

    const onTouchMove = (e: TouchEvent) => {
      if (contentDragStartY.current === null) return;

      const currentY = e.touches[0].clientY;
      const deltaY = contentDragStartY.current - currentY;
      const { isAtTop, isAtBottom } = initialScrollState.current;
      const currentSnap = snapRef.current;

      const applyDrag = () => {
        if (!isContentDragging.current) {
          isContentDragging.current = true;
          dragStartY.current = currentY;
          dragStartSnap.current = currentSnap;
          setIsDragging(true);
        }
        const containerHeight = container.clientHeight;
        if (!containerHeight) return;
        const deltaRatio = (dragStartY.current! - currentY) / containerHeight;
        setSnap(clamp(dragStartSnap.current + deltaRatio));
      };

      // 가장 낮은 스냅: 본문 스크롤 대신 위로 스와이프 = 시트 확장.
      if (currentSnap <= minSnap) {
        e.preventDefault();
        if (deltaY > 5) applyDrag();
        return;
      }

      const shouldShrink = isAtTop && deltaY < -5;
      const shouldExpand = isAtBottom && deltaY > 5;
      if (shouldShrink || shouldExpand) {
        e.preventDefault();
        applyDrag();
      }
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [scrollEl, containerRef, clamp, minSnap]);

  return {
    snap,
    setSnap,
    isDragging,
    handleProps: {
      onTouchStart: (e: React.TouchEvent) => handleDragStart(e.touches[0].clientY),
      onTouchMove: (e: React.TouchEvent) => handleDragMove(e.touches[0].clientY),
      onTouchEnd: handleDragEnd,
      onMouseDown: handleMouseDown,
    },
    attachScroll,
    contentProps: {
      onTouchStart: handleContentTouchStart,
      onTouchEnd: handleContentTouchEnd,
    },
  };
}
